import { getInstance } from './index';
import { watch, nextTick } from 'vue';
import piniaInstance from '@/stores';
import { useGlobalState } from '@/stores/globalState';
import { useAuth0 } from '@auth0/auth0-vue';
export const authGuard = (to, from, next) => {
    const auth0 = useAuth0();
    const { isAuthenticated } = auth0;

    const authService = getInstance();

    if (!authService) {
        console.error('Auth service was not initialized before authGuard ran');
        return next(false);
    }

    const globalState = useGlobalState(piniaInstance);

    const userIsAuthorizedForPage = () => {
        if (to.meta.god && authService.role.includes('God')) {
            return true;
        }

        if (to.meta.admin && authService.role.includes('SuperAdmin')) {
            return true;
        }

        if (to.meta.owner && (authService.role.includes('Owner') || authService.role.includes('RegionalManager'))) {
            return true;
        }

        if (Object.keys(to.meta).length === 0) {
            return true;
        }

        return false;
    };

    const getHomeRedirectPath = () => {
        if (authService.role.includes('SuperAdmin')) {
            return '/homes';
        }

        if (authService.role.includes('Supplier')) {
            return `/supplier-settings/${authService.user['https://memoryshare.com/supplier_id']}?tab=products`;
        }

        return '/services';
    };

    // Impersonation keeps the browser history from the SuperAdmin session, so Back can land on an
    // admin-only page the impersonated user can't see. Send them to their own landing page instead.
    const getImpersonationLandingPath = () => (authService.impersonationTarget === 'Owner' ? '/homes' : '/services');

    const badApiClaim = () => {
        return (
            authService.impersonating !== true &&
            authService.user?.['https://memoryshare.com/api'] !== import.meta.env.VUE_APP_API &&
            process.env.NODE_ENV === 'production'
        );
    };

    const guardAction = () => {
        if (!isAuthenticated.value) {
            // If the user IS NOT authenticated, log in
            auth0.loginWithRedirect({ appState: { target: to.fullPath } });
            return;
        } else {
            if (to.name === 'Wrapped') {
                let extFhId = authService?.session?.home?.externalId;

                if (extFhId) {
                    // window.location.href = `https://wrapped.memoryshare.com/${extFhId}`;
                    window.open(`https://wrapped.memoryshare.com/${extFhId}`, '_blank');
                    return;
                }
            }

            //If the user IS authenticated, continue with the route

            // Set top nav visibility
            if (to.name === 'MigrationNotification') {
                globalState.toggleTopNav(false);
            } else if (!globalState.topNavShowing) {
                globalState.toggleTopNav(true);
            }

            //Handle home route dynamic redirect
            if (to.fullPath === '/') {
                const homeRedirectPath = getHomeRedirectPath();
                return next({ path: homeRedirectPath });
            }

            // If user is a funeral home but the API claim isn't right, redirect them to error page
            if (authService.role.includes('FuneralHome') && badApiClaim()) {
                return next({ name: 'MigrationNotification' });
            }

            //Check user role / page meta authorization
            let allowThrough = userIsAuthorizedForPage();

            if (allowThrough) {
                return next();
            } else if (authService.impersonating) {
                return next({ path: getImpersonationLandingPath() });
            } else {
                return next({ name: 'PathNotFound' });
            }
        }
    };

    // If loading has already finished, check our auth state using `guardAction()`
    if (!authService.loading) {
        return guardAction();
    }

    // Watch for the loading property to change before we check isAuthenticated
    watch(
        () => authService.loading,
        async loading => {
            if (loading === false) {
                await nextTick();
                return guardAction();
            }
        },
    );
};
